import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { LinkedinIcon, ChevronDownIcon, GlobeIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { leaders, Leader } from '../data/leaders';
function LeaderCard({
  leader,
  index
}: {
  leader: Leader;
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const [bioOpen, setBioOpen] = useState(false);
  const {
    language,
    t
  } = useLanguage();
  const displayTitle = language === 'ES' ? leader.titleEs : leader.title;
  const displayBio = language === 'ES' ? leader.bioEs : leader.bio;
  return <motion.article ref={ref} initial={{
    opacity: 0,
    y: 50,
    rotate: index % 2 === 0 ? -2 : 2
  }} animate={inView ? {
    opacity: 1,
    y: 0,
    rotate: 0
  } : {}} transition={{
    duration: 0.7,
    delay: index * 0.1,
    ease: [0.16, 1, 0.3, 1]
  }} className={`bg-white rounded-2xl shadow-lg overflow-hidden border-l-4 ${leader.borderColor} group card-hover`} data-id="element-2723" data-mp="23s4ux5c9oaat">
      <div className="p-6" data-id="element-2724" data-mp="yijk21vj25si">
        {leader.photo ? <div className="mb-4" data-id="element-2725" data-mp="tpqfbe026xgf">
            <motion.div whileHover={{
          scale: 1.02
        }} className={`w-full h-64 rounded-2xl bg-gradient-to-br ${leader.gradient} overflow-hidden shadow-lg`} data-id="element-2726" data-mp="tpgpprixesb9">
              <img src={leader.photo} alt={leader.name} className="w-full h-full object-cover" style={{
            objectPosition: leader.photoObjectPosition ?? 'center 15%',
            transform: leader.photoTransform,
            transformOrigin: leader.photoTransformOrigin
          }} data-id="element-2727" data-mp="13i5vq6vpjdz7" />
            </motion.div>
            <div className="mt-4" data-id="element-2728" data-mp="1i4ydhcfdpcpx">
              <h3 className="font-bebas text-xl text-haa-navy tracking-wider leading-tight" data-id="element-2729" data-mp="1i5zxi6edh9js">
                {leader.name}
              </h3>
              <p className={`font-nunito font-bold text-sm ${leader.accentColor} uppercase tracking-wider`} data-id="element-2730" data-mp="1n65kvadm09w2">
                {displayTitle}
              </p>
              {(leader.jobTitle || leader.jobTitleEs) && <p className="font-nunito text-xs text-haa-midblue font-semibold tracking-wide mt-0.5" data-id="element-2731" data-mp="214lxtj22ewmn">
                  {language === 'ES' ? leader.jobTitleEs : leader.jobTitle}
                </p>}
              {leader.confirmed === false && <span className="font-nunito text-xs text-gray-400 font-normal tracking-normal block mt-0.5" data-id="element-2732" data-mp="2fsqksz85hub8">
                  {language === 'ES' ? '(Confirmación Pendiente)' : '(Confirmation Pending)'}
                </span>}
            </div>
          </div> : <div className="flex items-start gap-4 mb-4" data-id="element-2733" data-mp="d6bhclgtj5xw">
            <motion.div whileHover={{
          scale: 1.05,
          rotate: 5
        }} className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${leader.gradient} flex items-center justify-center flex-shrink-0 shadow-lg overflow-hidden`} data-id="element-2734" data-mp="d3oyvqse360w">
              <span className="font-bebas text-white text-2xl tracking-wider" data-id="element-2735" data-mp="3ve0xifsvwfla">
                {leader.initials}
              </span>
            </motion.div>
            <div className="flex-1 min-w-0" data-id="element-2736" data-mp="rw8zajo5mzen">
              <h3 className="font-bebas text-xl text-haa-navy tracking-wider leading-tight" data-id="element-2737" data-mp="rxbqyttax5vm">
                {leader.name}
              </h3>
              <p className={`font-nunito font-bold text-sm ${leader.accentColor} uppercase tracking-wider`} data-id="element-2738" data-mp="i438vedjwq1k">
                {displayTitle}
              </p>
              {(leader.jobTitle || leader.jobTitleEs) && <p className="font-nunito text-xs text-haa-midblue font-semibold tracking-wide mt-0.5" data-id="element-2739" data-mp="1arz2ga7tq1u4">
                  {language === 'ES' ? leader.jobTitleEs : leader.jobTitle}
                </p>}
              {leader.confirmed === false && <span className="font-nunito text-xs text-gray-400 font-normal tracking-normal block mt-0.5" data-id="element-2740" data-mp="lexpy8u17m3v">
                  {language === 'ES' ? '(Confirmación Pendiente)' : '(Confirmation Pending)'}
                </span>}
            </div>
          </div>}

        {/* Bio Dropdown Toggle */}
        <button onClick={() => setBioOpen(!bioOpen)} aria-expanded={bioOpen} className={`w-full flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl mb-1 text-left transition-colors duration-200 ${bioOpen ? 'bg-gray-50' : 'hover:bg-gray-50'}`} data-id="element-2741" data-mp="14cqgscrds5kw">
          <span className={`font-nunito font-semibold text-sm ${leader.accentColor}`} data-id="element-2742" data-mp="21rmasntzehkp">
            {bioOpen ? t('leadership.hideBio') : t('leadership.readBio')}
          </span>
          <motion.div animate={{
          rotate: bioOpen ? 180 : 0
        }} transition={{
          duration: 0.25,
          ease: [0.16, 1, 0.3, 1]
        }} className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-gradient-to-br ${leader.gradient}`} data-id="element-2743" data-mp="275chrtzttdp2">
            <ChevronDownIcon className="w-3.5 h-3.5 text-white" data-id="element-2744" data-mp="1s83p8wyse3vr" />
          </motion.div>
        </button>

        {/* Animated Bio */}
        <AnimatePresence initial={false} data-id="element-2745" data-mp="225zytkc2sqrr">
          {bioOpen && <motion.div key="bio" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.35,
          ease: [0.16, 1, 0.3, 1]
        }} className="overflow-hidden" data-id="element-2746" data-mp="227b27j1lzwic">
              <p className="font-nunito text-gray-600 text-sm leading-relaxed pt-2 pb-4 px-1" data-id="element-2747" data-mp="3kq7l2vyuwfmj">
                {displayBio}
              </p>
              {leader.profileSlug && <div className="px-1 pb-4" data-id="element-2748" data-mp="3ulrdh8e7ow6j">
                  <a href={`#/member/${leader.profileSlug}`} className={`inline-flex items-center gap-1.5 font-nunito font-bold text-sm ${leader.accentColor} hover:opacity-80 transition-opacity`} data-id="element-2749" data-mp="2wzajod4ot0pj">
                    {language === 'ES' ? 'Ver Perfil Completo' : 'View Full Profile'}
                    <span className="text-lg leading-none" data-id="element-2750" data-mp="2e8kg0m7al9vm">→</span>
                  </a>
                </div>}
            </motion.div>}
        </AnimatePresence>

        {/* Social Links */}
        <div className="flex items-center gap-2 pt-4 border-t border-gray-100" data-id="element-2751" data-mp="2boecgfmlblfl">
          {leader.linkedin && <motion.a href={leader.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${leader.name} LinkedIn`} whileHover={{
          scale: 1.15
        }} whileTap={{
          scale: 0.9
        }} className={`w-8 h-8 rounded-lg bg-gradient-to-br ${leader.gradient} flex items-center justify-center`} data-id="element-2752" data-mp="26ptb2cvlqvpk">
              <LinkedinIcon className="w-4 h-4 text-white" data-id="element-2753" data-mp="1iz746oxc3hh5" />
            </motion.a>}
          {leader.website && <motion.a href={leader.website} target="_blank" rel="noopener noreferrer" aria-label={`${leader.name} Website`} whileHover={{
          scale: 1.15
        }} whileTap={{
          scale: 0.9
        }} className={`w-8 h-8 rounded-lg bg-gradient-to-br ${leader.gradient} flex items-center justify-center`} data-id="element-2754" data-mp="1e0vtkbz7t2xv">
              <GlobeIcon className="w-4 h-4 text-white" data-id="element-2755" data-mp="2xt4alizqflrp" />
            </motion.a>}
          {leader.website2 && <motion.a href={leader.website2} target="_blank" rel="noopener noreferrer" aria-label={`${leader.name} Featured Article`} whileHover={{
          scale: 1.15
        }} whileTap={{
          scale: 0.9
        }} className={`w-8 h-8 rounded-lg bg-gradient-to-br ${leader.gradient} flex items-center justify-center`} data-id="element-2756" data-mp="2nwrao3dlgboi">
              <GlobeIcon className="w-4 h-4 text-white" data-id="element-2757" data-mp="254f9vl2l9igh" />
            </motion.a>}
        </div>
      </div>
    </motion.article>;
}
export function Leadership() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const {
    t
  } = useLanguage();
  return <section id="leadership" className="bg-gray-50 py-10 lg:py-14" aria-labelledby="leadership-heading" data-id="element-2758" data-mp="17kyn6ypoc60r">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2759" data-mp="1lkztwusme7o5">
        <div ref={titleRef} className="text-center mb-14" data-id="element-2760" data-mp="1gln01q3c00c2">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-2761" data-mp="1gk3jdydrjn9r">
            <span className="font-nunito text-haa-blue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-2762" data-mp="209dx56ty7y58">
              {t('leadership.label')}
            </span>
            <h2 id="leadership-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2" data-id="element-2763" data-mp="29ac3hvrv0hqz">
              {t('leadership.heading')}
            </h2>
            <div className="flex items-center justify-center gap-3 mt-4" data-id="element-2764" data-mp="6os5uzl3tac2">
              <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-2765" data-mp="2ngf2qqhuj2a" />
              <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-2766" data-mp="2nopmbz59cab" />
              <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-2767" data-mp="3tusup1bqaog3" />
            </div>
            <p className="font-nunito text-gray-600 text-lg mt-6 max-w-2xl mx-auto" data-id="element-2768" data-mp="uew935udr82j">
              {t('leadership.description')}
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2769" data-mp="ldodivj90lc5">
          {leaders.map((leader, i) => <LeaderCard key={leader.name} leader={leader} index={i} data-id="element-2770" data-mp="gfmweg5duw3b" data-mpi={i} />)}
        </div>
      </div>
    </section>;
}